import React from 'react'
import { IGenericBriefStepOption } from '../../types'
import { optionKeyToKebabCase } from '../../utils'
import { RadioTileWithImage } from '@components/common/RadioTileWithImage'
import {
  usePreloadedAnswers,
  useReadOnlyMode,
} from '../BriefingFormDataProvider'
import parentLogger from '@utils/logger'
import * as Sentry from '@sentry/nextjs'

const logger = parentLogger.child({
  component: 'BriefButtonTileSelection',
})

interface IBriefButtonTileSelectionOptions {
  options: IGenericBriefStepOption[]
  valueKey: string
  preselectedOptionKey: string
  onChange: (value) => void
}

export function BriefButtonTileSelection({
  options,
  valueKey,
  onChange,
  preselectedOptionKey = undefined,
}: IBriefButtonTileSelectionOptions) {
  const readOnlyMode = useReadOnlyMode()
  const preloadedAnswers = usePreloadedAnswers()
  const formattedValueKey = optionKeyToKebabCase(valueKey)

  // Default selection logic is a bit complex:
  // -- if user edits his selection for the item that is already in the cart, it will be dictated by the preloadedAnswers
  // -- if user edits new item, it will be dictated by the preselectedOptionKey property
  // -- if preselectedOptionKey is missing, it will be set to the first option in the array
  const preloadedOption = preloadedAnswers?.[formattedValueKey]
    ? options.find(
        (option) => option.value === preloadedAnswers[formattedValueKey]
      )
    : null
  const defaultOptionKey = preloadedOption?.key ?? preselectedOptionKey
  const defaultOption = defaultOptionKey
    ? options?.find((option) => option?.key === defaultOptionKey)
    : options?.[0]

  const onSelectionChange = (
    selectedOption: IGenericBriefStepOption | null
  ) => {
    if (!formattedValueKey) {
      Sentry.setContext('briefingFormQuestion', options)
      Sentry.captureException(
        'Briefing Form Question Key is missing, falling back to option label'
      )
      logger.error(
        'Briefing Form Question Key is missing, falling back to option label'
      )
    }
    if (readOnlyMode) return

    logger.debug({ selectedOption }, 'Briefing Form Answer')

    onChange({
      [formattedValueKey || optionKeyToKebabCase(selectedOption?.label)]:
        selectedOption?.value,
    })
  }

  return (
    <RadioTileWithImage
      options={options}
      onChange={onSelectionChange}
      defaultOption={defaultOption}
    />
  )
}
